import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { getClientDetails, RenewalTask } from '../src/Utils/renewalTaskApi';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Building2, Calendar, AlertTriangle, User, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { ROUTES } from '../src/routes/config';
import { TaskDetailsPanel } from './TaskDetailsPanel';
import { Sheet, SheetContent } from './ui/sheet';

export const ClientDetailsView: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState<string>('');
  const [totalTasks, setTotalTasks] = useState(0);
  const [tasksByStatus, setTasksByStatus] = useState<{
    OPEN: RenewalTask[];
    IN_PROGRESS: RenewalTask[];
    WAITING: RenewalTask[];
    RESOLVED: RenewalTask[];
    CLOSED: RenewalTask[];
  }>({
    OPEN: [],
    IN_PROGRESS: [],
    WAITING: [],
    RESOLVED: [],
    CLOSED: [],
  });
  const [allTasks, setAllTasks] = useState<RenewalTask[]>([]);
  const [selectedTask, setSelectedTask] = useState<RenewalTask | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !clientId) return;

    const fetchClientDetails = async () => {
      setLoading(true);
      try {
        const response = await getClientDetails(clientId, token);

        if (response.success) {
          setClientName(response.data.clientName);
          setTotalTasks(response.data.totalTasks);
          setTasksByStatus(response.data.tasksByStatus);
          
          // Sort all tasks by priority and due date
          const sortedTasks = response.data.allTasks.sort((a, b) => {
            const priorityOrder = { P0: 0, P1: 1, P2: 2, P3: 3, P4: 4 };
            const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
            if (priorityDiff !== 0) return priorityDiff;

            if (a.dueDate && b.dueDate) {
              return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
            }
            if (a.dueDate) return -1;
            if (b.dueDate) return 1;
            return 0;
          });

          setAllTasks(sortedTasks);
        } else {
          toast.error('Failed to load client details');
        }
      } catch (error: any) {
        console.error('Error fetching client details:', error);
        toast.error(error.message || 'Failed to load client details');
      } finally {
        setLoading(false);
      }
    };

    fetchClientDetails();
  }, [token, clientId]);

  const getPriorityBadge = (priority: RenewalTask['priority']) => {
    const variants: Record<string, { className: string; label: string }> = {
      P0: { className: 'bg-red-100 text-red-800 border-red-300', label: 'P0' },
      P1: { className: 'bg-orange-100 text-orange-800 border-orange-300', label: 'P1' },
      P2: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'P2' },
      P3: { className: 'bg-blue-100 text-blue-800 border-blue-300', label: 'P3' },
      P4: { className: 'bg-gray-100 text-gray-800 border-gray-300', label: 'P4' },
    };

    const config = variants[priority] || variants.P4;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const getStatusBadge = (status: RenewalTask['status']) => {
    const variants: Record<string, { className: string }> = {
      CLOSED: { className: 'bg-green-100 text-green-800 border-green-300' },
      RESOLVED: { className: 'bg-green-100 text-green-800 border-green-300' },
      IN_PROGRESS: { className: 'bg-blue-100 text-blue-800 border-blue-300' },
      WAITING: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      OPEN: { className: 'bg-gray-100 text-gray-800 border-gray-300' },
    };

    const config = variants[status] || variants.OPEN;
    return <Badge variant="outline" className={config.className}>{status}</Badge>;
  };

  const displayTasks = selectedStatusFilter
    ? tasksByStatus[selectedStatusFilter as keyof typeof tasksByStatus] || []
    : allTasks;

  // Group tasks by section
  const tasksBySection = displayTasks.reduce((acc, task) => {
    const section = task.sectionName || 'Uncategorized';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(task);
    return acc;
  }, {} as Record<string, RenewalTask[]>);

  // Sort sections and tasks within each section
  const sortedSections = Object.keys(tasksBySection).sort();
  sortedSections.forEach((section) => {
    tasksBySection[section].sort((a, b) => {
      const priorityOrder = { P0: 0, P1: 1, P2: 2, P3: 3, P4: 4 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;

      if (a.dueDate && b.dueDate) {
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      }
      if (a.dueDate) return -1;
      if (b.dueDate) return 1;
      return 0;
    });
  });

  const overdueCount = allTasks.filter(
    (t) => t.bucket === 'OVERDUE' || (t.dueDate && new Date(t.dueDate) < new Date())
  ).length;
  const criticalCount = allTasks.filter((t) => t.priority === 'P0').length;
  const completedCount = tasksByStatus.CLOSED.length + tasksByStatus.RESOLVED.length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.supervisorDashboard)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              {clientName || 'Client Details'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Comprehensive task overview and status breakdown
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-600">Loading client details...</span>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Total Tasks</p>
                    <p className="text-3xl font-bold">{totalTasks}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-500 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Completed</p>
                    <p className="text-3xl font-bold">{completedCount}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-green-500 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Overdue</p>
                    <p className="text-3xl font-bold">{overdueCount}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-500 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Critical (P0)</p>
                    <p className="text-3xl font-bold">{criticalCount}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-500 opacity-20" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Status Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Tasks by Status</CardTitle>
              <CardDescription>Click on a status to filter tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedStatusFilter === null ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedStatusFilter(null)}
                >
                  All ({allTasks.length})
                </Button>
                <Button
                  variant={selectedStatusFilter === 'OPEN' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedStatusFilter('OPEN')}
                >
                  Open ({tasksByStatus.OPEN.length})
                </Button>
                <Button
                  variant={selectedStatusFilter === 'IN_PROGRESS' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedStatusFilter('IN_PROGRESS')}
                >
                  In Progress ({tasksByStatus.IN_PROGRESS.length})
                </Button>
                <Button
                  variant={selectedStatusFilter === 'WAITING' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedStatusFilter('WAITING')}
                >
                  Waiting ({tasksByStatus.WAITING.length})
                </Button>
                <Button
                  variant={selectedStatusFilter === 'RESOLVED' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedStatusFilter('RESOLVED')}
                >
                  Resolved ({tasksByStatus.RESOLVED.length})
                </Button>
                <Button
                  variant={selectedStatusFilter === 'CLOSED' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedStatusFilter('CLOSED')}
                >
                  Closed ({tasksByStatus.CLOSED.length})
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tasks Table */}
          <Card>
            <CardHeader>
              <CardTitle>
                {selectedStatusFilter ? `${selectedStatusFilter} Tasks` : 'All Tasks'}
              </CardTitle>
              <CardDescription>
                {displayTasks.length} task{displayTasks.length !== 1 ? 's' : ''} shown
              </CardDescription>
            </CardHeader>
            <CardContent>
              {displayTasks.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No tasks found</p>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead className="text-center">Priority</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Start Date</TableHead>
                        <TableHead className="text-center">End Date</TableHead>
                        <TableHead className="text-center">Due Date</TableHead>
                        <TableHead className="text-center">Bucket</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedSections.map((section) => (
                        <React.Fragment key={section}>
                          {/* Section Header Row */}
                          <TableRow className="bg-gray-50 hover:bg-gray-50">
                            <TableCell
                              colSpan={8}
                              className="font-semibold text-sm text-gray-700 py-3"
                            >
                              {section} ({tasksBySection[section].length} task{tasksBySection[section].length !== 1 ? 's' : ''})
                            </TableCell>
                          </TableRow>
                          {/* Tasks in this section */}
                          {tasksBySection[section].map((task) => (
                            <TableRow
                              key={task.id}
                              className="cursor-pointer hover:bg-gray-50"
                              onClick={() => setSelectedTask(task)}
                            >
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  {task.bucket === 'OVERDUE' && (
                                    <AlertTriangle className="w-4 h-4 text-red-500" />
                                  )}
                                  <span>{task.itemName}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                {task.owner ? (
                                  <div className="flex items-center gap-2">
                                    <User className="w-3 h-3 text-gray-400" />
                                    <span className="text-sm">
                                      {task.owner.firstName && task.owner.lastName
                                        ? `${task.owner.firstName} ${task.owner.lastName}`
                                        : task.owner.email}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {getPriorityBadge(task.priority)}
                              </TableCell>
                              <TableCell className="text-center">
                                {getStatusBadge(task.status)}
                              </TableCell>
                              <TableCell className="text-center">
                                {task.startDate ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <Calendar className="w-3 h-3 text-gray-400" />
                                    <span className="text-sm">
                                      {format(new Date(task.startDate), 'MMM d, yyyy')}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {task.endDate ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <Calendar className="w-3 h-3 text-gray-400" />
                                    <span className="text-sm">
                                      {format(new Date(task.endDate), 'MMM d, yyyy')}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {task.dueDate ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <Calendar className="w-3 h-3 text-gray-400" />
                                    <span className="text-sm">
                                      {format(new Date(task.dueDate), 'MMM d, yyyy')}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {task.bucket === 'OVERDUE' ? (
                                  <Badge variant="destructive">Overdue</Badge>
                                ) : (
                                  <Badge variant="outline">{task.bucket}</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Sheet open={!!selectedTask} onOpenChange={(open) => !open && setSelectedTask(null)}>
        <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto p-0">
          {selectedTask && (
            <TaskDetailsPanel
              task={selectedTask}
              onUpdate={() => setSelectedTask(null)}
              onClose={() => setSelectedTask(null)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

